import { useMemo } from "react";
import { Typography } from "@mui/material";
import { type GridRowId } from "@mui/x-data-grid-pro";
import {
  ALL_MATTERS_FIELDS,
  CHECKBOX_FIELDS,
  isAllMattersField,
} from "../utils/checkboxFields";
import {
  useCheckedVersion,
  type CheckedIdsStore,
} from "../hooks/useCheckedIdsStore";

// 全部事項の4列（共担/信託/現在/閉鎖）は1つの書類の構成オプションのため、
// 行内にいくつチェックがあっても全部事項1件と数える。それ以外の列は1チェック=1件
// targetIds 省略時は全件集計（絞り込みフィルタが不要なため Set のコピー自体を避ける）
const countCheckedDocuments = (
  store: CheckedIdsStore,
  targetIds?: (id: GridRowId) => boolean
): number => {
  const allMattersRowIds = new Set<GridRowId>(
    ALL_MATTERS_FIELDS.flatMap((field) => {
      const ids = store.getField(field);
      return targetIds ? [...ids].filter(targetIds) : [...ids];
    })
  );
  return CHECKBOX_FIELDS.reduce(
    (sum, { field }) =>
      isAllMattersField(field)
        ? sum
        : sum +
          (targetIds
            ? [...store.getField(field)].filter(targetIds).length
            : store.getField(field).size),
    allMattersRowIds.size
  );
};

/**
 * 選択中の書類総数の表示。チェックは絞り込みをまたいで維持されるため、
 * 絞り込みで非表示になっている行のチェック分は内訳として添える
 * （見えていない行が依頼対象に含まれることを実行前に知らせる）。
 * チェック状態の購読をこの部品に閉じ、チェック操作で親を再レンダリングさせない
 */
export const BulkCheckedCount: React.FC<{
  store: CheckedIdsStore;
  /** 表示中の行ID。null は全行表示（読み込み前含む） */
  visibleRowIds: Set<GridRowId> | null;
}> = ({ store, visibleRowIds }) => {
  const checkedVersion = useCheckedVersion(store);
  // checkedVersion 依存でチェック変更のたびに再計算する
  const totalCount = useMemo(
    () => countCheckedDocuments(store),
    [store, checkedVersion]
  );
  const hiddenCount = useMemo(
    () =>
      visibleRowIds == null
        ? 0
        : totalCount -
          countCheckedDocuments(store, (id) => visibleRowIds.has(id)),
    // totalCount ではなく checkedVersion に依存する。合計が変わらないチェックの
    // 入れ替え（非表示行を外して表示中の行に入れる等）でも内訳は変わるため
    [store, visibleRowIds, checkedVersion]
  );
  return (
    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
      選択中: {totalCount.toLocaleString()}件
      {hiddenCount > 0 &&
        `（うち絞り込みで非表示: ${hiddenCount.toLocaleString()}件）`}
    </Typography>
  );
};
